/**
 * <BrainPanel> — embedded AI brief + chat for Inventory / Production pages.
 *
 * Props:
 *   kind: 'inventory' | 'production'
 *   navigate: react-router navigate fn (used to deep-link to Intelligence Suite)
 *
 * Behaviour:
 *   - On mount: fetches /api/brains/{kind}. Server returns cached brief
 *     instantly (≤ 200 ms); if stale, server kicks a background regen and
 *     marks `regenerating: true`. We poll every 30 s while `regenerating`.
 *   - "Refresh" button forces /refresh and shows spinner.
 *   - Inline chat input — POSTs to /api/brains/{kind}/chat. Renders the latest
 *     question + answer; pressing ↑/↓ does NOT cycle history (kept simple).
 *   - Headline + summary_text + critical/blocked count chips up top.
 *   - Snapshot rows are NOT rendered here — that lives on the Inventory /
 *     Production pages already; this widget focuses on the AI layer.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Brain, RefreshCw, Send, ChevronDown, ChevronUp, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const KIND_META = {
  inventory: {
    title: 'Inventory Brain',
    blurb: 'Auto-refreshing AI brief — stock, lead-time, ETAs, and what to order before stockout.',
    accent: 'from-emerald-500/15 to-cyan-500/10 border-emerald-500/40',
    iconColor: 'text-emerald-300',
    placeholder: 'Ask: which RMs should I order this week? When does Cumin run out?',
    suggestedQs: [
      'Which 3 RMs should I order today to avoid production stoppage next week?',
      'Which materials are arriving in the next 7 days?',
      'Which RMs are below reorder level AND have nothing inbound?',
    ],
  },
  production: {
    title: 'Production Brain',
    blurb: 'Plans next 14 days using RM stock, lead times, inbound POs, dispatch dues, and machine maintenance.',
    accent: 'from-amber-500/15 to-rose-500/10 border-amber-500/40',
    iconColor: 'text-amber-300',
    placeholder: 'Ask: what should each line run tomorrow? Which orders are blocked?',
    suggestedQs: [
      'What should each line run for the next 7 days?',
      'Which orders are blocked because RM won\'t arrive in time?',
      'Plan dispatch sequencing for orders due this week.',
    ],
  },
};

export default function BrainPanel({ kind, navigate }) {
  const meta = KIND_META[kind];
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [question, setQuestion] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [history, setHistory] = useState([]); // [{q, a, ts}]
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/brains/${kind}`, { withCredentials: true });
      setBrief(res.data);
      // If server says it's regenerating, poll every 30s until fresh
      if (res.data?.regenerating) {
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(load, 30000);
      }
    } catch (e) {
      toast.error('Brain unavailable: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line
  }, [kind]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/brains/${kind}/refresh`, {}, { withCredentials: true });
      toast.success(`${meta.title} refresh started — updating in background…`);
      // Mark as regenerating + start polling so the chip shows up immediately
      setBrief(b => ({ ...(b || {}), regenerating: true }));
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(load, 15000);
    } catch (e) {
      toast.error('Refresh failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  const ask = async (q) => {
    const text = (q || question || '').trim();
    if (!text || chatLoading) return;
    setChatLoading(true);
    try {
      const res = await axios.post(
        `${API}/brains/${kind}/chat`,
        { question: text },
        { withCredentials: true }
      );
      setHistory(h => [{ q: text, a: res.data.answer, ts: new Date().toISOString() }, ...h].slice(0, 5));
      setQuestion('');
    } catch (e) {
      toast.error('AI failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setChatLoading(false);
    }
  };

  const generatedAt = brief?.generated_at;
  const ageStr = generatedAt ? _ageHuman(generatedAt) : '—';

  const summary = brief?.summary_text || '';
  const isLlmError = summary.startsWith('AI brief unavailable') || summary.startsWith('AI unavailable');

  return (
    <div className={`bg-gradient-to-br ${meta.accent} border rounded-2xl p-4 sm:p-5 mb-5`} data-testid={`brain-panel-${kind}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-9 h-9 rounded-xl bg-zinc-950/60 border border-zinc-800 flex items-center justify-center ${meta.iconColor} flex-shrink-0`}>
            <Brain size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-zinc-50 text-sm flex items-center gap-2">
              {meta.title}
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-950/80 border border-zinc-700 text-zinc-400 font-mono">
                {ageStr}
              </span>
              {brief?.regenerating && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 flex items-center gap-1">
                  <RefreshCw size={9} className="animate-spin" /> regenerating
                </span>
              )}
            </div>
            <div className="text-zinc-400 text-[11px] mt-0.5 truncate">{meta.blurb}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button onClick={refresh} disabled={refreshing} data-testid={`brain-refresh-${kind}`}
            className="flex items-center gap-1 text-xs bg-zinc-950/70 hover:bg-zinc-900 text-zinc-200 border border-zinc-700 px-2.5 py-1.5 rounded-lg disabled:opacity-50">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => navigate('/intelligence-suite')} data-testid={`brain-open-suite-${kind}`}
            className="flex items-center gap-1 text-xs bg-zinc-950/70 hover:bg-zinc-900 text-zinc-200 border border-zinc-700 px-2.5 py-1.5 rounded-lg">
            <ExternalLink size={12} /> Open Full AI
          </button>
          <button onClick={() => setCollapsed(c => !c)} data-testid={`brain-toggle-${kind}`}
            className="flex items-center gap-1 text-xs bg-zinc-950/70 hover:bg-zinc-900 text-zinc-200 border border-zinc-700 px-2.5 py-1.5 rounded-lg">
            {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Headline */}
          {loading ? (
            <div className="text-zinc-500 text-xs italic">Loading brain…</div>
          ) : (
            <>
              <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3 mb-3">
                <div className="text-zinc-100 text-sm font-semibold flex items-center gap-2 flex-wrap">
                  {brief?.headline || 'No headline'}
                  {kind === 'inventory' && (brief?.critical_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40 font-mono">
                      {brief.critical_count} critical
                    </span>
                  )}
                  {kind === 'inventory' && (brief?.high_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 font-mono">
                      {brief.high_count} high
                    </span>
                  )}
                  {kind === 'production' && (brief?.blocked_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40 font-mono">
                      {brief.blocked_count} blocked
                    </span>
                  )}
                  {kind === 'production' && (brief?.ready_count ?? 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-mono">
                      {brief.ready_count} ready
                    </span>
                  )}
                </div>
                {summary && (
                  <pre className={`whitespace-pre-wrap text-xs mt-2.5 font-sans leading-relaxed ${isLlmError ? 'text-amber-300' : 'text-zinc-300'}`} data-testid={`brain-summary-${kind}`}>
                    {summary}
                  </pre>
                )}
              </div>

              {/* Suggested questions */}
              <div className="flex flex-wrap gap-1.5 mb-2.5">
                {meta.suggestedQs.map((q, i) => (
                  <button key={i} onClick={() => ask(q)} disabled={chatLoading}
                    data-testid={`brain-suggested-q-${kind}-${i}`}
                    className="text-[10.5px] px-2 py-1 rounded-md bg-zinc-950/60 hover:bg-zinc-900 border border-zinc-700 text-zinc-300 disabled:opacity-50">
                    <Sparkles size={9} className="inline mr-1 -mt-0.5" /> {q}
                  </button>
                ))}
              </div>

              {/* Chat input */}
              <div className="flex items-center gap-2">
                <input
                  value={question}
                  onChange={e => setQuestion(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && ask()}
                  placeholder={meta.placeholder}
                  disabled={chatLoading}
                  data-testid={`brain-input-${kind}`}
                  className="flex-1 bg-zinc-950/70 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 disabled:opacity-50"
                />
                <button onClick={() => ask()} disabled={chatLoading || !question.trim()}
                  data-testid={`brain-ask-${kind}`}
                  className="flex items-center gap-1 px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg text-sm disabled:opacity-40">
                  {chatLoading ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
                  Ask
                </button>
              </div>

              {/* History */}
              {history.length > 0 && (
                <div className="mt-3 space-y-2.5 max-h-[400px] overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={i} className="bg-zinc-950/60 border border-zinc-800 rounded-lg p-2.5" data-testid={`brain-history-${kind}-${i}`}>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">You</div>
                      <div className="text-zinc-200 text-xs mb-2">{h.q}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">AI</div>
                      <pre className="whitespace-pre-wrap text-zinc-300 text-xs font-sans leading-relaxed">{h.a}</pre>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function _ageHuman(iso) {
  try {
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return '—';
  }
}
